/**
 * Pluggable remote-storage backend interface.
 *
 * `sync.ts` orchestrates push/pull/conflict logic against this interface; each
 * concrete backend (Gist today, others to come) only handles transport.
 *
 * File names (`patterns.json`, `sessions.json`) are domain-level and live in
 * `sync.ts`. Backends just see an opaque `Record<string, string>` of files and
 * an opaque cursor (e.g. ETag, revision) for change detection.
 */

export class RemoteNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RemoteNotFoundError';
  }
}

export interface RemoteSnapshot {
  /** Sparse: a key may be absent if the remote does not have that file. */
  files: Record<string, string | undefined>;
  cursor: string;
  updatedAt: string;
}

export interface CreateResult {
  remoteId: string;
  cursor: string;
  updatedAt: string;
}

export interface UpdateResult {
  cursor: string;
  updatedAt: string;
}

export interface RemoteBackend {
  readonly kind: BackendKind;

  create(files: Record<string, string>): Promise<CreateResult>;

  /** Returns null when the remote has not changed since `prevCursor`. */
  fetch(remoteId: string, prevCursor?: string): Promise<RemoteSnapshot | null>;

  /** Sparse: only the keys present in `files` are written. */
  update(remoteId: string, files: Record<string, string>): Promise<UpdateResult>;
}

// ─── Backend configurations ──────────────────────────────────────────────
//
// Per-instance state (remoteId, cursor, lastSyncedAt) lives on the config
// itself so switching backends naturally drops it without leaking across
// kinds.

export type BackendKind = 'gist';

export interface BackendInstanceState {
  remoteId?: string;
  cursor?: string;
  lastSyncedAt?: string;
}

export interface GistBackendConfig extends BackendInstanceState {
  kind: 'gist';
  pat: string;
}

export type BackendConfig = GistBackendConfig;
